'use strict';

/* globals jQuery Stripe yithStripePayments yithStripePaymentsElement */

import { $, $body, block, unblock } from '../globals';

/**
 * Class that init and manages all interactions with Stripe Element
 */
export default class PaymentElement {
	/**
	 * Flag enabled when we're on checkout or pay page
	 */
	isCheckout;

	/**
	 * Flag enabled when we're on add payment method page
	 */
	isAddMethod;

	/**
	 * jQuery element that reference target of embed
	 */
	$target;

	/**
	 * Id for the embed target
	 */
	target = '#yith-stripe-payments-element';

	/**
	 * Stores Stripe object once created
	 */
	stripe;

	/**
	 * Store Elements object once created
	 */
	elements;

	/**
	 * Store PaymentElement object once created
	 */
	paymentElement;

	/**
	 * Constructor method
	 */
	constructor() {
		// instance binding
		this.maybeInitElements = this.maybeInitElements.bind( this );
		this.updateElements = this.updateElements.bind( this );
		this.onSubmit = this.onSubmit.bind( this );
		this.onError = this.onError.bind( this );
		this.onHashChange = this.onHashChange.bind( this );
		this.afterElementsSubmit = this.afterElementsSubmit.bind( this );
		this.clearPaymentMethod = this.clearPaymentMethod.bind( this );

		// init object
		this.init();
	}

	/* === INITIALIZATION METHOD === */

	/**
	 * Init current object
	 */
	init() {
		this.initStripe();
		this.maybeInitElements();
		this.initFormSubmit();
		this.initHashChange();

		$body.on( 'payment_method_selected', this.maybeInitElements );
	}

	/**
	 * Init Stripe object
	 */
	initStripe() {
		const { public_key: publicKey, account_id: acctId } =
			yithStripePaymentsElement;

		if ( ! publicKey || ! acctId ) {
			return;
		}

		this.stripe = Stripe( publicKey, {
			stripeAccount: acctId,
		} );
	}

	/**
	 * Init checkout handling
	 */
	initFormSubmit() {
		this.getForm().on( `submit`, this.onSubmit );
	}

	/**
	 */
	initHashChange() {
		window.addEventListener( 'hashchange', this.onHashChange );

		this.onHashChange();
	}

	/* === ELEMENTS HANDLING === */

	/**
	 * Init Elements if currently selected payment method is Stripe Payments Element
	 */
	maybeInitElements() {
		const { slug } = yithStripePaymentsElement;

		const $selectedMethod = $( 'input[name="payment_method"]:checked' ),
			selectedMethod = $selectedMethod.attr( 'id' );

		if ( `payment_method_${ slug }` !== selectedMethod ) {
			return;
		}

		// init elements.
		this.initElements();
	}

	/**
	 * Init Elements given that conditions are matched
	 *
	 * @return {Promise<void>}
	 */
	async initElements() {
		if ( ! this.stripe ) {
			return;
		}

		this.elements = this.stripe.elements( await this.getElementsDetails() );
		this.paymentElement = this.elements
			.create( 'payment', {
				layout: yithStripePaymentsElement.layout,
			} )
			.on( 'change', this.clearPaymentMethod );
		this.mount();
	}

	/**
	 * Update Elements with new Checkout details
	 *
	 * @return {Promise<void>}
	 */
	async updateElements() {
		if ( ! this.stripe || ! this.elements ) {
			return;
		}

		this.elements.update( await this.getElementsDetails() );
		this.clearPaymentMethod();
		this.mount();
	}

	/**
	 * Submit Elements form and waits for response
	 */
	submitElements() {
		if ( ! this.stripe || ! this.elements ) {
			return;
		}

		block( this.getForm() );

		this.elements
			.submit()
			.then( this.afterElementsSubmit )
			.catch( this.onError );
	}

	/**
	 * Handles successful responses from Elements submit
	 */
	afterElementsSubmit() {
		// otherwise proceed creating a payment method.
		this.stripe
			.createPaymentMethod( {
				elements: this.elements,
				params: {
					billing_details: this.getBillingDetails(),
				},
			} )
			.then( ( { error, paymentMethod } ) => {
				unblock( this.getForm() );

				if ( error ) {
					this.onError( error );
					return;
				}

				this.appendPaymentMethod( paymentMethod );
				this.getForm().trigger( 'submit' );
			} )
			.catch( this.onError );
	}

	/**
	 * Mount Elements on target node
	 */
	mount() {
		this.paymentElement.mount( this.getTargetNode( true ) );
	}

	/* === FORM HANDLING === */

	/**
	 * Returns container form
	 *
	 * @return {jQuery} Container form
	 */
	getForm() {
		const $target = this.getTarget();

		return $target.closest( 'form' );
	}

	/**
	 * Handles container form submit
	 */
	onSubmit() {
		if ( ! this.hasPaymentMethod() ) {
			this.submitElements();
			return false;
		}
	}

	/**
	 * Prints error messages relevant to customer
	 *
	 *
	 * @param {Object} error Error describing current error.
	 */
	onError( error ) {
		const $form = this.getForm();

		unblock( $form );

		// remove existing error messages.
		$form.find( '.woocommerce-error' ).remove();

		// add new error message.
		const $errorMsg = $( '<div/>', {
			class: 'woocommerce-error',
			text: error.message,
		} );
		$form.prepend( $errorMsg );

		// scroll to notice.
		$.scroll_to_notices( $errorMsg );

		// trigger form fields validation.
		$form
			.find( '.input-text, select, input:checkbox' )
			.trigger( 'validate' )
			.trigger( 'blur' );
	}

	/* === PAYMENT METHOD HANDLING === */

	/**
	 * Checks if current form already has a payment method or needs a new one
	 *
	 * @return {boolean} Whether form contains payment method or not
	 */
	hasPaymentMethod() {
		const $paymentMethod = this.getPaymentMethod();

		return $paymentMethod.length && !! $paymentMethod.val();
	}

	/**
	 * Returns payment method Node
	 *
	 * @return {jQuery} jQuery node for the Payment Method hidden field
	 */
	getPaymentMethod() {
		const $form = this.getForm();

		return $form.find(
			`#yith-stripe-payments-${ yithStripePaymentsElement.slug }-payment-method`
		);
	}

	/**
	 * Set value for the Payment method input in the form.
	 * If input does not exist, creates it and append it to the form
	 *
	 * @param {Object} paymentMethod Payment method object
	 */
	appendPaymentMethod( paymentMethod ) {
		let $paymentMethod = this.getPaymentMethod();

		if ( ! $paymentMethod.length ) {
			const $form = this.getForm();

			$paymentMethod = $( '<input/>', {
				type: 'hidden',
				id: `yith-stripe-payments-${ yithStripePaymentsElement.slug }-payment-method`,
				name: `yith_stripe_payments_${ yithStripePaymentsElement.slug }_payment_method`,
				value: paymentMethod.id,
			} );

			$form.append( $paymentMethod );
		} else {
			$paymentMethod.val( paymentMethod.id );
		}
	}

	/**
	 * Clears payment method input, to make sure we don't accidentally process checkout with an outdated one
	 */
	clearPaymentMethod() {
		const $paymentMethod = this.getPaymentMethod();

		if ( ! $paymentMethod.length ) {
			return;
		}

		$paymentMethod.val( '' );
	}

	/* === HANDLE NEXT ACTIONS === */

	/**
	 * Reacts to hash change in the url
	 */
	async onHashChange() {
		const hash = window.location.hash;

		if ( ! hash ) {
			return;
		}

		const identifier = `yith-stripe-payments-${ yithStripePaymentsElement.slug }`,
			regex = new RegExp( `^#${ identifier }/confirm/([^/]+)/(.+)$` ),
			matches = hash.match( regex );

		if ( ! matches || 3 < matches.length ) {
			return;
		}

		window.location.hash = '';

		const [ , clientSecret, redirectUrl ] = matches;

		this.stripe
			.handleNextAction( {
				clientSecret,
			} )
			.then( ( result ) => {
				result.error && this.onError( result.error );
				result.paymentIntent && ( window.location = redirectUrl );
			} )
			.catch( this.onError );
	}

	/* === GETTERS METHOD === */

	/**
	 * Get an object with billing details about customer
	 *
	 * @return {Object} Billing object.
	 */
	getBillingDetails() {
		return {};
	}

	/**
	 * Returns jQuery node to the embed target
	 *
	 * @param {boolean} refresh Whether to refresh target or not
	 * @return {jQuery} jQuery node element.
	 */
	getTarget( refresh ) {
		if ( ! this.$target || refresh ) {
			this.$target = $( this.target );
		}

		return this.$target;
	}

	/**
	 * Returns DOM node to the embed target
	 *
	 * @param {boolean} refresh Whether to refresh target or not
	 * @return {Element} DOM node element.
	 */
	getTargetNode( refresh ) {
		return this.getTarget( refresh ).get( 0 );
	}

	/**
	 * Returns an object with a set of options to be used for Elements init/update
	 *
	 * @return {Promise<object|boolean>} Settings object, or false on failure.
	 */
	async getElementsDetails() {
		return {
			mode: 'setup',
			currency: yithStripePaymentsElement.currency,
		};
	}
}
